<template>
  <Box>
    <form class="dialog" onsubmit="return false" @keydown.esc="() => handleCloseAttempt()">
      <div class="header has-bcc-background has-text-white">
        <span>{{ questionnaireData.name }}</span>
        <span title="Close Questionnaire">
          <i class="fas fa-times" style="cursor: pointer; color: #cd0000; font-size: 22px !important" aria-hidden="true" @click="() => handleCloseAttempt()" />
        </span>
      </div>

      <div class="body" id="dialog-body">
        <Loading v-if="isLoading" />

        <div v-else>
          <div class="instructions-section">
            <div class="columns">
              <div class="instructions-section__instruction column is-8">
                <h1>Instructions:</h1>
                <span v-html="questionnaireData.portalInstructions" />
              </div>

              <div class="instructions-section__invitees column is-4">
                <h1>Invitees:</h1>
                <div v-for="invitee in questionnaireData.currentInvitees" :key="invitee.id">
                  <span
                    ><small style="color: #d7d7d7">●</small> {{ invitee.fullName }} <small>&lt;{{ invitee.email }}&gt;</small></span
                  ><br />
                </div>
              </div>
            </div>
          </div>

          <!-- Carousel Tab System -->
          <div class="carousel-container">
            <!-- Tab Navigation -->
            <div class="tabs is-boxed" style="overflow: unset">
              <ul>
                <li :class="{ 'is-active': activeTab === 'company' }">
                  <a @click="activeTab = 'company'" class="tab-link">
                    <span class="icon is-small"><i class="fas fa-building" aria-hidden="true"></i></span>
                    <span>Bank Contact Information</span>
                    <span v-if="bankContactInformationErrors > 0" class="error-badge">
                      {{ bankContactInformationErrors }}
                    </span>
                  </a>
                </li>
                <li :class="{ 'is-active': activeTab === 'participants' }">
                  <a @click="activeTab = 'participants'" class="tab-link">
                    <span class="icon is-small"><i class="fas fa-users" aria-hidden="true"></i></span>
                    <span>Participants</span>
                    <span v-if="participantErrorCount > 0" class="error-badge">
                      {{ participantErrorCount }}
                    </span>
                  </a>
                </li>
                <li :class="{ 'is-active': activeTab === 'contacts' }">
                  <a @click="activeTab = 'contacts'" class="tab-link">
                    <span class="icon is-small"><i class="fas fa-address-book" aria-hidden="true"></i></span>
                    <span>Contacts</span>
                    <span v-if="contactErrorCount > 0" class="error-badge">
                      {{ contactErrorCount }}
                    </span>
                  </a>
                </li>
              </ul>
            </div>

            <!-- Carousel Content -->
            <div class="carousel-content">
              <div class="carousel-track" :style="{ transform: `translateX(-${carouselPosition}%)` }">
                <!-- Bank Contact Information Panel -->
                <div class="carousel-panel">
                  <div class="tab-header">
                    <div class="tab-header-content">
                      <div>
                        <h3>Bank Contact Information</h3>
                        <p>Please review and update bank contact details as needed.</p>
                      </div>

                      <button class="button is-light is-small" @click="resetClientData()">
                        <span class="icon is-small">
                          <i class="fas fa-undo"></i>
                        </span>
                        <span>Reset to Last Saved</span>
                      </button>
                    </div>
                  </div>
                  <div class="tab-body">
                    <div class="form-section" style="margin-bottom: 12px; overflow-x: auto">
                      <div class="field-group" style="width: 99.2% !important">
                        <div class="columns">
                          <!-- Legal Address -->
                          <div class="column is-4">
                            <h5 class="has-text-weight-bold is-flex">
                              <i class="fas fa-lock" style="font-size: 10px; margin-right: 6px" />
                              Legal Address &nbsp;
                              <span style="font-size: 10px; color: #fff; background-color: #3c557c; border-radius: 50px; padding: 2px 8px; font-weight: 400; display: flex; align-items: center">From S&P Global</span>
                            </h5>
                            <p style="font-size: 10px; color: #666; margin: -12px 0px 22px 0">
                              Read-only. Sourced from S&P Global as of {{ questionnaireData.spGlobalAsOfDate }}. Insurance Carrier documents are mailed here unless you edit the Mailing Address.
                            </p>

                            <div class="field">
                              <label class="label">Address</label>
                              <div class="control">
                                <input :value="bankContactInformation.primaryAddress.street" disabled class="input is-small" :class="{ 'is-danger': !bankContactValidation.primaryAddress.street }" type="text" placeholder="Enter address" />
                              </div>
                            </div>
                            <div class="field">
                              <label class="label">City</label>
                              <div class="control">
                                <input :value="bankContactInformation.primaryAddress.city" disabled class="input is-small" type="text" placeholder="Enter city" :class="{ 'is-danger': !bankContactValidation.primaryAddress.city }" />
                              </div>
                            </div>
                            <div class="field">
                              <div class="columns is-gapless">
                                <div class="column is-8">
                                  <label class="label">State</label>
                                  <div class="control">
                                    <input :value="bankContactInformation.primaryAddress.state" disabled class="input is-small" type="text" placeholder="Enter state" :class="{ 'is-danger': !bankContactValidation.primaryAddress.state }" />
                                  </div>
                                </div>
                                <div class="column is-4" style="padding-left: 0.5rem">
                                  <label class="label">Zip Code</label>
                                  <div class="control">
                                    <input
                                      :value="bankContactInformation.primaryAddress.postalCode"
                                      v-maska="'#####-####'"
                                      disabled
                                      class="input is-small"
                                      type="text"
                                      placeholder="Enter zip"
                                      :class="{ 'is-danger': !bankContactValidation.primaryAddress.postalCode }"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Mailing Address -->
                          <div class="column is-4">
                            <h5 class="has-text-weight-bold is-flex" style="margin-bottom: 0">
                              Mailing Address &nbsp;
                              <span style="font-size: 10px; color: #fff; background-color: #3c557c; border-radius: 50px; padding: 2px 8px; font-weight: 400; display: flex; align-items: center">Optional</span>
                            </h5>
                            <p style="font-size: 10px; color: #666; margin: 4px 0 10px 0px">Only needed if you want Insurance Carrier documents mailed somewhere other than your Legal Address.</p>

                            <div class="field">
                              <div class="control">
                                <label class="checkbox">
                                  <input v-model="changeMailingAddress" type="checkbox" />
                                  Change Mailing Address
                                </label>
                              </div>
                            </div>
                            <div class="field">
                              <label class="label">Address</label>
                              <div class="control">
                                <input
                                  v-model="bankContactInformation.mailingAddress.street"
                                  class="input is-small"
                                  type="text"
                                  placeholder="Enter address"
                                  :disabled="!changeMailingAddress"
                                  :class="{ 'is-danger': !bankContactValidation.mailingAddress.street }"
                                />
                              </div>
                            </div>
                            <div class="field">
                              <label class="label">City</label>
                              <div class="control">
                                <input
                                  v-model="bankContactInformation.mailingAddress.city"
                                  class="input is-small"
                                  type="text"
                                  placeholder="Enter city"
                                  :disabled="!changeMailingAddress"
                                  :class="{ 'is-danger': !bankContactValidation.mailingAddress.city }"
                                />
                              </div>
                            </div>
                            <div class="field">
                              <div class="columns is-gapless">
                                <div class="column is-8">
                                  <label class="label">State</label>
                                  <div class="control">
                                    <input
                                      v-model="bankContactInformation.mailingAddress.state"
                                      class="input is-small"
                                      type="text"
                                      placeholder="Enter state"
                                      :disabled="!changeMailingAddress"
                                      :class="{ 'is-danger': !bankContactValidation.mailingAddress.state }"
                                    />
                                  </div>
                                </div>
                                <div class="column is-4" style="padding-left: 0.5rem">
                                  <label class="label">Zip Code</label>
                                  <div class="control">
                                    <input
                                      v-maska="'#####-####'"
                                      v-model="bankContactInformation.mailingAddress.postalCode"
                                      class="input is-small"
                                      type="text"
                                      placeholder="Enter zip"
                                      :disabled="!changeMailingAddress"
                                      :class="{ 'is-danger': !bankContactValidation.mailingAddress.postalCode }"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Contact Information -->
                          <div class="column is-4">
                            <h5 class="has-text-weight-bold">Contact Information</h5>

                            <div class="field">
                              <label class="label">Main Phone</label>
                              <div class="control">
                                <input v-model="bankContactInformation.clientPhone" class="input is-small" type="tel" placeholder="(xxx) xxx-xxxx" v-maska="'(###) ###-####'" :class="{ 'is-danger': !bankContactValidation.clientPhone }" />
                              </div>
                            </div>
                            <div class="field">
                              <label class="label">Main Fax</label>
                              <div class="control">
                                <input v-model="bankContactInformation.clientFax" class="input is-small" type="tel" placeholder="(xxx) xxx-xxxx" v-maska="'(###) ###-####'" />
                              </div>
                            </div>
                            <div class="field">
                              <label class="label">TIN</label>
                              <div class="control">
                                <input v-model="bankContactInformation.clientTIN" v-maska="'##-#######'" class="input is-small" type="text" placeholder="00-0000000" :class="{ 'is-danger': !bankContactValidation.clientTIN }" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Participants Panel -->
                <div class="carousel-panel">
                  <div class="tab-header">
                    <div class="tab-header-content">
                      <div>
                        <h3>Participant Information</h3>
                        <p>Please review and adjust participant entries as needed.</p>
                      </div>
                      <button class="button is-light is-small" @click="resetParticipantData()">
                        <span class="icon is-small">
                          <i class="fas fa-undo"></i>
                        </span>
                        <span>Reset to Last Saved</span>
                      </button>
                    </div>
                  </div>
                  <div class="tab-body">
                    <small style="font-size: 10px; color: #666; margin-left: 58%">* If copying and pasting, dates must be in the format of 'YYYY-MM-DD'</small>
                    <VGrid
                      apply-on-close
                      ref="participantGrid"
                      class="grid"
                      :theme="'compact'"
                      :source="participantData"
                      :columns="participantColumns"
                      :autoSizeColumn="true"
                      :column-types="gridColumnTypes"
                      filter
                      range
                      resize
                      row-headers
                      hide-attribution
                    />
                  </div>
                </div>

                <!-- Contacts Panel -->
                <div class="carousel-panel">
                  <div class="tab-header">
                    <div class="tab-header-content">
                      <div>
                        <h3>Contact Information</h3>
                        <p>Please review and adjust contact entries as needed. Contacts listed below are user(s) who have access to the BCC Client Portal, or user(s) who should be added or removed.</p>
                      </div>

                      <button class="button is-light is-small" @click.prevent="addNewContact" type="button">
                        <span class="icon is-small">
                          <i class="fas fa-plus"></i>
                        </span>
                        <span>Add Contact</span>
                      </button>
                      &nbsp;
                      <button class="button is-light is-small" @click="resetContactData()">
                        <span class="icon is-small">
                          <i class="fas fa-undo"></i>
                        </span>
                        <span>Reset to Last Saved</span>
                      </button>
                    </div>
                  </div>
                  <div class="tab-body">
                    <VGrid apply-on-close ref="contactGrid" class="grid" :theme="'compact'" :source="contactData" :columns="contactColumns" :autoSizeColumn="true" :column-types="gridColumnTypes" filter range resize row-headers hide-attribution />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Review Confirmation Section -->
          <!-- <div class="review-confirmation-section">
            <label class="checkbox">
              <input v-model="reviewConfirmed" type="checkbox" />
              I have reviewed and updated the relevant information on this Annual Questionnaire.
            </label>
          </div> -->
        </div>
      </div>

      <div class="footer">
        <button style="outline: none" :disabled="isSubmitting || isLoading" type="button" class="button is-light has-text-weight-semibold is-small" @click="() => downloadSpreadsheet()">
          <span class="icon is-small">
            <i class="fas fa-download"></i>
          </span>
          <span> Download as Excel </span>
        </button>

        <label for="upload-file-input" class="button is-light has-text-weight-semibold is-small upload-label">
          <span class="icon is-small">
            <i class="fas fa-upload"></i>
          </span>
          <span> Upload Excel </span>
        </label>
        <input :disabled="isSubmitting || isLoading" ref="fileInput" type="file" id="upload-file-input" style="display: none" @change="(e) => uploadSpreadsheet(e.target.files[0])" accept=".xlsx, .xls" />

        <div style="width: 1px; background-color: #aaa; margin: 0 5px" />

        <button :disabled="isSubmitting || isLoading" style="outline: none" type="button" class="button has-bcc-hover-background has-text-white has-text-weight-semibold is-small" @click="() => handleSave(false, false, false)">
          <span class="icon is-small">
            <i class="fas fa-save"></i>
          </span>
          <span> Save </span>
        </button>

        <button :disabled="isSubmitting || isLoading" style="outline: none" type="button" class="button has-bcc-background has-text-white has-text-weight-semibold is-small" @click="() => (showSubmitConfirmation = true)">
          <span class="icon is-small">
            <i class="fas fa-check"></i>
          </span>
          <span> Submit Questionnaire </span>
        </button>

        <div style="width: 1px; background-color: #aaa; margin: 0 5px" />

        <button :disabled="isSubmitting || isLoading" style="outline: none" type="button" class="button is-light has-text-weight-semibold is-small" @click="() => handleCloseAttempt()">
          <span class="icon is-small has-text-danger">
            <i class="fas fa-times"></i>
          </span>
          <span> Cancel </span>
        </button>
      </div>
    </form>

    <!-- Close Confirmation Modal -->
    <div class="modal" :class="{ 'is-active': showCloseConfirmation }">
      <div class="modal-background" @click="showCloseConfirmation = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Confirm Close</p>
          <button class="delete" aria-label="close" @click="showCloseConfirmation = false"></button>
        </header>
        <section class="modal-card-body">
          <div class="content">
            <p><strong>Are you sure you want to close without saving?</strong></p>
            <p>Any unsaved changes will be lost. You can save your progress and continue later if needed.</p>
          </div>
        </section>
        <footer class="is-flex is-justify-content-space-around modal-card-foot">
          <button class="button" @click="showCloseConfirmation = false">Continue Editing</button>
          <button class="button" @click="() => handleModalSaveForLater()"><i class="fas fa-save"></i>&nbsp; Save Before Closing</button>
          <button class="button is-danger" @click="confirmClose">Close Without Saving</button>
        </footer>
      </div>
    </div>

    <!-- Submit Confirmation Modal -->
    <div class="modal" :class="{ 'is-active': showSubmitConfirmation }">
      <div class="modal-background" @click="showSubmitConfirmation = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Review Confirmation</p>
          <button class="delete" aria-label="close" @click="showSubmitConfirmation = false"></button>
        </header>
        <section class="modal-card-body">
          <div class="content">
            <label class="checkbox">
              <input v-model="reviewConfirmed" type="checkbox" />
              I have reviewed and updated the relevant information on this Annual Questionnaire.
            </label>
          </div>
        </section>
        <footer class="is-flex is-justify-content-flex-end modal-card-foot">
          <button
            class="button"
            @click="
              () => {
                showSubmitConfirmation = false
                reviewConfirmed = false
              }
            "
          >
            Continue Editing
          </button>
          <button class="button has-bcc-background has-text-white has-text-weight-semibold" @click="handleSubmit" :disabled="!reviewConfirmed">
            <span class="icon">
              <i class="fa fa-send-o"></i>
            </span>
            <span> Submit Questionnaire </span>
          </button>
        </footer>
      </div>
    </div>
  </Box>
</template>

<script setup>
import Box from './Box.vue'
import { closeDialog } from 'vue3-promise-dialog'
import { computed, onMounted, ref, nextTick, watch, onBeforeMount, onUnmounted } from 'vue'
import { store, logOut } from '../../store'
import Constants from '../../constants'
import moment from 'moment-timezone'
import Loading from '../Loading.vue'
import VGrid from '@revolist/vue3-datagrid'
import NumberColumnType from '@revolist/revogrid-column-numeral'
import { useGlobalUtils } from '../../helpers/globalUtils'
import axios from '../../axios'

const numeral = NumberColumnType.getNumeralInstance()
const { isEmail, errorToast, successToast } = useGlobalUtils()

// Register custom phone format with numeral only if not already registered
if (!numeral.formats.phone) {
  numeral.register('format', 'phone', {
    regexps: {
      format: /(\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{4})/,
      unformat: /[^\d]/g,
    },
    format: function (value) {
      if (!value) return ''

      // Convert to string and remove all non-digits
      const digits = `${value}`.replace(/\D/g, '').slice(0, 10) || ''

      if (digits.length === 0) return ''
      if (digits.length <= 3) {
        return `(${digits}`
      } else if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
      } else {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
      }
    },
    unformat: function (string) {
      if (!string) return 0
      // Remove all non-digit characters and convert to number
      const digits = string.replace(/\D/g, '')
      return digits ? parseFloat(digits) : 0
    },
  })
}

const { terminationReasons } = Constants

const props = defineProps({
  rawQuestionnaireData: {
    required: true,
    default: null,
  },
})

const isLoading = ref(true)
const activeTab = ref('company')
const questionnaireData = ref({})
const gridColumnTypes = ref({})
const reviewConfirmed = ref(false)
const participantData = ref([])
const contactData = ref([])
const participantColumns = ref([])
const contactColumns = ref([])
const rawOriginalData = ref({})
const rawData = ref({})
const fileInput = ref(null)
const changeMailingAddress = ref(false)
const bankContactInformationErrors = ref(0)
const showSubmitConfirmation = ref(false)
const isSubmitting = ref(false)
const autoSaveIntervalMs = 5 * 60 * 1000
const autosaveTimer = ref(null)
const isAutoSaving = ref(false)

const startAutoSave = () => {
  console.info('Auto-save started...')
  if (autosaveTimer.value) return
  autosaveTimer.value = window.setInterval(async () => {
    if (isLoading.value || isAutoSaving.value) return
    isAutoSaving.value = true
    console.info('Performing auto-save...')
    try {
      await handleSave(false, true, false)
    } catch (err) {
      console.error('Auto-save failed:', err)
    } finally {
      isAutoSaving.value = false
    }
  }, autoSaveIntervalMs)
}

const stopAutoSave = () => {
  if (!autosaveTimer.value) return
  clearInterval(autosaveTimer.value)
  autosaveTimer.value = null
}

const downloadSpreadsheet = async () => {
  isLoading.value = true
  await axios
    .get(`/Questionnaires/${questionnaireData.value.questionnaireId}/Download`, {
      responseType: 'blob',
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Annual_Questionnaire_${questionnaireData.value.name.replace(/\s+/g, '_')}_${moment().format('YYYY')}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      successToast('Annual Questionnaire downloaded successfully.')
    })
    .catch((error) => {
      console.error('Error downloading Annual Questionnaire sheet:', error)
      errorToast('Failed to download Annual Questionnaire sheet. Please try again later.')
    })
    .finally(() => {
      isLoading.value = false
      handleGrids()
    })
}

const handleModalSaveForLater = async () => {
  await handleSave(false, false, true)
  nextTick(() => {
    showCloseConfirmation.value = false
  })
}

const uploadSpreadsheet = async (file) => {
  isLoading.value = true
  const formData = new FormData()
  formData.append('file', file)

  await axios
    .post(`/Questionnaires/${questionnaireData.value.questionnaireId}/Upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(async (response) => {
      if (response.status >= 200 && response.status < 300) {
        rawData.value.participants = response.data.participants || []
        rawData.value.contacts = response.data.contacts || []
        rawData.value.clientData = response.data.clientData || {}
        rawData.value.clientData.mailingAddressChanged ? (changeMailingAddress.value = true) : (changeMailingAddress.value = false)
        const errors = response.data.errors || []
        await getQuestionnaireData()

        successToast('Annual Questionnaire sheet uploaded successfully.')

        if (errors.length > 0) {
          let errorMessage = 'Some issues were found in the uploaded file:<br/><br/>'
          errors.forEach((error, index) => {
            errorMessage += `${index + 1}. ${error.sheet} Sheet: ${error.column}${error.row} - ${error.error}<br/>`
          })
          errorToast(errorMessage)
        }
      }
    })
    .catch((error) => {
      console.error('Error uploading Annual Questionnaire sheet:', error)
      errorToast('Failed to upload Annual Questionnaire sheet. Please ensure the file is valid and try again.')
    })
    .finally(() => {
      isLoading.value = false

      if (fileInput.value) {
        fileInput.value.value = null
        handleGrids()
      }
    })
}

const bankContactInformation = ref({
  primaryAddress: {
    street: '',
    city: '',
    postalCode: '',
    state: '',
  },
  mailingAddress: {
    street: '',
    city: '',
    postalCode: '',
    state: '',
  },
  clientPhone: '',
  clientFax: '',
  clientTIN: '',
})

const bankContactValidation = ref({
  primaryAddress: {
    street: true,
    city: true,
    postalCode: true,
    state: true,
  },
  mailingAddress: {
    street: true,
    city: true,
    postalCode: true,
    state: true,
  },
  clientPhone: true,
  clientTIN: true,
})

// Add validation functions
const validateBankContactField = (section, field, value) => {
  if (section === 'clientPhone') {
    if (value) {
      const phoneDigits = value?.toString().replace(/\D/g, '')
      return phoneDigits.length === 10 // Phone is required, must be exactly 10 digits
    } else {
      return false // Phone is required
    }
  }

  if (section === 'primaryAddress' || section === 'mailingAddress') {
    if (field === 'postalCode') {
      if (!value || value?.trim()?.length === 0) {
        return false // Postal code is required
      }
      const zipPattern = /^\d{5}(-\d{4})?$/
      return zipPattern.test(value)
    }
    // Required fields validation
    if (['street', 'city', 'state'].includes(field)) {
      return value?.trim()?.length > 0
    }
  }

  if (section === 'clientTIN') {
    if (!value || value?.trim()?.length === 0) {
      return false // TIN is required
    }
    const tinPattern = /^\d{2}-\d{7}$/
    return tinPattern.test(value)
  }

  return true
}

const validateAllBankContactFields = () => {
  // Validate primary address
  bankContactValidation.value.primaryAddress.street = validateBankContactField('primaryAddress', 'street', bankContactInformation.value.primaryAddress.street)
  bankContactValidation.value.primaryAddress.city = validateBankContactField('primaryAddress', 'city', bankContactInformation.value.primaryAddress.city)
  bankContactValidation.value.primaryAddress.state = validateBankContactField('primaryAddress', 'state', bankContactInformation.value.primaryAddress.state)
  bankContactValidation.value.primaryAddress.postalCode = validateBankContactField('primaryAddress', 'postalCode', bankContactInformation.value.primaryAddress.postalCode)

  const { street, city, state, postalCode } = rawData.value.clientData.mailingAddress
  if (street || city || state || postalCode || changeMailingAddress.value === true) {
    bankContactValidation.value.mailingAddress.street = validateBankContactField('mailingAddress', 'street', bankContactInformation.value.mailingAddress.street)
    bankContactValidation.value.mailingAddress.city = validateBankContactField('mailingAddress', 'city', bankContactInformation.value.mailingAddress.city)
    bankContactValidation.value.mailingAddress.state = validateBankContactField('mailingAddress', 'state', bankContactInformation.value.mailingAddress.state)
    bankContactValidation.value.mailingAddress.postalCode = validateBankContactField('mailingAddress', 'postalCode', bankContactInformation.value.mailingAddress.postalCode)
  }

  // Validate phone
  bankContactValidation.value.clientPhone = validateBankContactField('clientPhone', null, bankContactInformation.value.clientPhone)

  // Validate TIN
  bankContactValidation.value.clientTIN = validateBankContactField('clientTIN', null, bankContactInformation.value.clientTIN)
}

const getBankContactValidationErrors = () => {
  let errors = 0

  // Count primary address errors
  Object.values(bankContactValidation.value.primaryAddress).forEach((isValid) => {
    if (!isValid) errors++
  })

  const { street, city, state, postalCode } = rawData.value.clientData.mailingAddress
  if (street || city || state || postalCode || changeMailingAddress.value === true) {
    Object.values(bankContactValidation.value.mailingAddress).forEach((isValid) => {
      if (!isValid) errors++
    })
  }

  // Count phone errors
  if (!bankContactValidation.value.clientPhone) errors++
  if (!bankContactValidation.value.clientTIN) errors++

  return errors
}

const windowWidth = computed(() => `${window.innerWidth - 5}px`)
const windowHeight = computed(() => `${window.innerHeight - window.innerHeight * 0.12}px`)

// Add carousel position computed property
const carouselPosition = computed(() => {
  switch (activeTab.value) {
    case 'company':
      return 0
    case 'participants':
      return 33.333
    case 'contacts':
      return 66.666
    default:
      return 0
  }
})

// Initialize grid columns and data
const initializeGrids = async () => {
  // Load column types
  gridColumnTypes.value = {
    date: new (await import('@revolist/revogrid-column-date')).default(),
    money: new NumberColumnType('$0,0.00'),
    select: new (await import('@revolist/revogrid-column-select')).default(),
    tel: new NumberColumnType('(000) 000-0000'),
  }

  const showCompensationColumns = computed(() => questionnaireData.value?.showCompensationColumns)

  const calculateColumnWidth = () => {
    const containerWidth = window.innerWidth - 140
    const participantColumnCount = showCompensationColumns.value ? 11 : 9
    const contactColumnCount = 8

    const res = {
      participant: Math.floor(containerWidth / participantColumnCount) < 150 ? 150 : Math.floor(containerWidth / participantColumnCount),
      contact: Math.floor(containerWidth / contactColumnCount) < 150 ? 150 : Math.floor(containerWidth / contactColumnCount),
    }

    return res
  }

  const columnWidths = calculateColumnWidth()

  // Participant columns configuration with calculated widths
  participantColumns.value = [
    {
      //First Name Column
      name: 'First Name',
      prop: 'firstName',
      title: 'First Name',
      size: columnWidths.participant,
      sortable: true,
      cellProperties: ({ model, prop }) => {
        model.isFirstNameValid = `${model[prop]}`.trim().length >= 2

        return {
          // Custom classes
          class: {
            'participant-first-name-validation-error': !model.isFirstNameValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Last Name Column
      name: 'Last Name',
      prop: 'lastName',
      title: 'Last Name',
      size: columnWidths.participant,
      sortable: true,
      cellProperties: ({ model, prop }) => {
        model.isLastNameValid = `${model[prop]}`.trim().length >= 2

        return {
          // Custom classes
          class: {
            'participant-last-name-validation-error': !model.isLastNameValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Title Column
      name: 'Title',
      prop: 'title',
      title: 'Title',
      size: columnWidths.participant,
      sortable: true,
      cellProperties: ({ model, prop }) => {
        model.isTitleValid = `${model[prop]}`.trim().length > 0

        return {
          // Custom classes
          class: {
            'participant-title-validation-error': !model.isTitleValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Employee ID Column
      name: 'Employee ID',
      prop: 'bankEmployeeNo',
      title: 'Employee ID',
      size: columnWidths.participant,
      sortable: true,
      cellProperties: ({ model, prop }) => {
        return {
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Branch/Department Column
      name: 'Branch/Department',
      prop: 'branchDepartment',
      title: 'Branch/Department',
      size: columnWidths.participant,
      sortable: true,
      cellProperties: ({ model, prop }) => {
        return {
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Hire Date Column
      name: 'Hire Date',
      filter: false,
      prop: 'hireDate',
      title: 'Hire Date',
      size: columnWidths.participant,
      sortable: true,
      columnType: 'date',
      cellProperties: ({ model, prop }) => {
        const dateObj = moment(model[prop])
        model.isHireDateValid = (dateObj.isValid() && typeof model[prop] === 'string') || model[prop] === null || `${model[prop]}` === 'null' || model[prop] === '' || typeof model[prop] === 'undefined'

        return {
          // Custom classes
          class: {
            'participant-hire-date-validation-error': !model.isHireDateValid,
            'participant-hire-date-field-value': true,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
          },
        }
      },
    },
    {
      //Termination Date Column
      name: 'Termination Date',
      filter: false,
      prop: 'terminationDate',
      title: 'Termination Date',
      size: columnWidths.participant,
      sortable: true,
      columnType: 'date',
      cellProperties: ({ model, prop }) => {
        const dateObj = moment(model[prop])
        model.isTerminationDateValid = (dateObj.isValid() && typeof model[prop] === 'string') || model[prop] === null || `${model[prop]}` === 'null' || model[prop] === '' || typeof model[prop] === 'undefined'

        return {
          // Custom classes
          class: {
            'participant-termination-date-validation-error': !model.isTerminationDateValid,
            'participant-termination-date-field-value': true,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Termination Reason Column
      name: 'Termination Reason',
      prop: 'terminationReason',
      filter: false,
      title: 'Termination Reason',
      size: columnWidths.participant,
      columnType: 'select',
      labelKey: 'label',
      valueKey: 'value',
      sortable: true,
      source: terminationReasons,
      cellProperties: ({ model, prop }) => {
        model.isTerminationReasonValid = terminationReasons.some((r) => `${r.value}` === `${model[prop]}`) || model[prop] === null || `${model[prop]}` === 'null' || model[prop] === '' || typeof model[prop] === 'undefined'

        return {
          // Custom classes
          class: {
            'participant-termination-reason-validation-error': !model.isTerminationReasonValid,
          },
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Base Salary Column
      name: 'Base Salary',
      prop: 'salary',
      title: 'Base Salary',
      filter: false,
      size: columnWidths.participant,
      columnType: 'money',
      sortable: true,
      readonly: (props) => {
        const isReadonly = isFieldReadonly(props.data[props.rowIndex])
        if (isReadonly) {
          participantData.value[props.rowIndex].salary = 0
          participantData.value[props.rowIndex].bonus = 0
          participantData.value[props.rowIndex].otherCompensation = 0
        }
        return isReadonly
      },
      cellProperties: ({ model, prop }) => {
        model.isSalaryValid = parseFloat(model[prop]) < 0 || (typeof model[prop] === 'string' && !isFieldReadonly(model)) ? false : true

        return {
          // Custom classes
          class: {
            'participant-salary-validation-error': !model.isSalaryValid || model?.hasSalaryError,
          },
          id: `participant-salary-field-${model.id}`,
          // Custom events
          onClick: () => setTimeout(() => handleBaseSalaryValidation(), 100),
          onBlur: () => setTimeout(() => handleBaseSalaryValidation(), 100),
          onFocusOut: () => setTimeout(() => handleBaseSalaryValidation(), 100),
        }
      },
    },
  ]

  // Add conditional columns
  if (showCompensationColumns.value) {
    participantColumns.value.push(
      {
        //Bonus Column
        name: 'Bonus',
        prop: 'bonus',
        filter: false,
        title: 'Bonus',
        size: columnWidths.participant,
        columnType: 'money',
        readonly: (props) => {
          const isReadonly = isFieldReadonly(props.data[props.rowIndex])
          if (isReadonly) {
            participantData.value[props.rowIndex].baseSalary = 0
            participantData.value[props.rowIndex].bonus = 0
            participantData.value[props.rowIndex].otherCompensation = 0
          }
          return isReadonly
        },
        sortable: true,
        cellProperties: ({ model, prop }) => {
          model.isBonusValid = parseFloat(model[prop]) < 0 || typeof model[prop] === 'string' ? false : true

          return {
            // Custom classes
            class: {
              'participant-bonus-validation-error': !model.isBonusValid,
            },
            // Custom events
            onClick: (event) => {
              event.stopPropagation()
              //console.log(model[prop])
            },
          }
        },
      },
      {
        //Other Compensation Column
        name: 'Other Compensation',
        filter: false,
        prop: 'otherCompensation',
        size: columnWidths.participant,
        title: 'Other Compensation',
        columnType: 'money',
        readonly: (props) => {
          const isReadonly = isFieldReadonly(props.data[props.rowIndex])
          if (isReadonly) {
            participantData.value[props.rowIndex].baseSalary = 0
            participantData.value[props.rowIndex].bonus = 0
            participantData.value[props.rowIndex].otherCompensation = 0
          }
          return isReadonly
        },
        sortable: true,
        cellProperties: ({ model, prop }) => {
          model.isOtherCompensationValid = parseFloat(model[prop]) < 0 || typeof model[prop] === 'string' ? false : true

          return {
            // Custom classes
            class: {
              'participant-other-compensation-validation-error': !model.isOtherCompensationValid,
            },
            // Custom events
            onClick: (event) => {
              event.stopPropagation()
              //console.log(model[prop])
            },
          }
        },
      }
    )
  }

  // Contact columns configuration with calculated widths
  contactColumns.value = [
    {
      //Primary Contact Column
      name: 'Primary Contact',
      prop: 'isPrimaryContact',
      title: 'Primary Contact',
      filter: false,
      size: columnWidths.contact,
      sortable: true,
      cellTemplate: (h, props) => {
        return h('input', {
          type: 'checkbox',
          checked: Boolean(props.value) || false,
          onChange: (e) => {
            // Update the data when checkbox changes
            const rowIndex = props.rowIndex
            contactData.value[rowIndex].isPrimaryContact = Boolean(e.target.checked)
            handleGrids()
          },
        })
      },
      cellProperties: ({ model, prop }) => {
        model.primaryContactValid = model[prop] === true || model[prop] === 'true' || model[prop] === 'false' || model[prop] === null || model[prop] === false || model[prop] === undefined

        return {
          // Custom classes
          class: {
            'contact-primary-contact-validation-error': !model.primaryContactValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //First Name Column
      name: 'First Name',
      title: 'First Name',
      prop: 'firstName',
      size: columnWidths.contact,
      sortable: true,
      cellProperties: ({ model, prop }) => {
        model.isFirstNameValid = `${model[prop]}`.trim().length >= 2

        return {
          // Custom classes
          class: {
            'contact-first-name-validation-error': !model.isFirstNameValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Last Name Column
      name: 'Last Name',
      title: 'Last Name',
      prop: 'lastName',
      size: columnWidths.contact,
      sortable: true,
      cellProperties: ({ model, prop }) => {
        model.isLastNameValid = `${model[prop]}`.trim().length >= 2

        return {
          // Custom classes
          class: {
            'contact-last-name-validation-error': !model.isLastNameValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Title Column
      name: 'Title',
      title: 'Title',
      prop: 'title',
      size: columnWidths.contact,
      sortable: true,
      cellProperties: ({ model, prop }) => {
        const { isDeleted } = model
        model.isTitleValid = isDeleted ? true : `${model[prop]}`.trim().length > 0

        return {
          // Custom classes
          class: {
            'contact-title-validation-error': !model.isTitleValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Phone Column
      name: 'Phone',
      title: 'Phone',
      prop: 'phone',
      size: columnWidths.contact,
      sortable: true,
      columnType: 'tel',
      cellProperties: ({ model, prop }) => {
        model.isPhoneValid = `${model[prop]}`.trim().replace(/\D/g, '').length === 10 || `${model[prop]}` === '' || !`${model[prop]}`.length || model[prop] === null || model[prop] === undefined

        return {
          // Custom classes
          class: {
            'contact-phone-validation-error': !model.isPhoneValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Extension Column
      name: 'Extension',
      title: 'Extension',
      prop: 'extension',
      size: columnWidths.contact,
      sortable: false,
      cellProperties: ({ model, prop }) => {
        model.isExtensionValid = `${model[prop]}`.trim().length > 0 || `${model[prop]}` === '' || !`${model[prop]}`.length || model[prop] === null || model[prop] === undefined

        return {
          // Custom classes
          class: {
            'contact-phone-ext-validation-error': !model.isExtensionValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Email Column
      name: 'Email',
      title: 'Email',
      prop: 'email',
      size: columnWidths.contact,
      sortable: true,
      cellProperties: ({ model, prop }) => {
        const { isDeleted } = model
        model.isEmailValid = isDeleted ? true : isEmail(model[prop])

        return {
          // Custom classes
          class: {
            'contact-email-validation-error': !model.isEmailValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //No Longer at Bank Column
      name: 'No Longer at Bank',
      prop: 'noLongerAtBank',
      title: 'No Longer at Bank',
      filter: false,
      size: columnWidths.contact,
      sortable: true,
      cellTemplate: (h, props) => {
        return h('input', {
          type: 'checkbox',
          checked: Boolean(props.value) || false,
          onChange: (e) => {
            // Update the data when checkbox changes
            const rowIndex = props.rowIndex
            contactData.value[rowIndex].noLongerAtBank = Boolean(e.target.checked)
            if (!contactData.value[rowIndex].isDeleted) {
              contactData.value[rowIndex].isDeleted = Boolean(e.target.checked)
            }
            handleGrids()
          },
        })
      },
      cellProperties: ({ model, prop }) => {
        model.noLongerAtBankValid = model[prop] === true || model[prop] === 'true' || model[prop] === 'false' || model[prop] === null || model[prop] === false || model[prop] === undefined

        return {
          // Custom classes
          class: {
            'contact-no-longer-at-bank-validation-error': !model.noLongerAtBankValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
    {
      //Is Deleted Column
      name: 'Remove Portal Access',
      prop: 'isDeleted',
      title: 'Remove Portal Access',
      filter: false,
      size: columnWidths.contact,
      sortable: true,
      cellTemplate: (h, props) => {
        return h('input', {
          type: 'checkbox',
          checked: Boolean(props.value) || false,
          onChange: (e) => {
            // Update the data when checkbox changes
            const rowIndex = props.rowIndex
            contactData.value[rowIndex].isDeleted = Boolean(e.target.checked)
            handleGrids()
          },
        })
      },
      cellProperties: ({ model, prop }) => {
        model.isDeletedValid = model[prop] === true || model[prop] === 'true' || model[prop] === 'false' || model[prop] === null || model[prop] === false

        return {
          // Custom classes
          class: {
            'contact-is-deleted-validation-error': !model.isDeletedValid,
          },
          // Custom events
          onClick: (event) => {
            event.stopPropagation()
            //console.log(model[prop])
          },
        }
      },
    },
  ]
}

const isFieldReadonly = (participant) => {
  const dateObj = moment(participant?.terminationDate)

  const hasTerminationDate = dateObj.isValid() && typeof participant?.terminationDate === 'string' && `${participant?.terminationDate}`.trim().length > 0
  const hasTerminationReason = participant?.terminationReason !== null && participant?.terminationReason !== undefined && participant?.terminationReason !== '' && participant?.terminationReason >= 0
  const hasTitleIndicatingTermination = participant?.title !== null && `${participant?.title}`?.trim()?.length > 0 && ['deceased', 'terminated', 'retired'].includes(`${participant?.title}`?.trim()?.toLowerCase())

  return hasTerminationDate || hasTerminationReason || hasTitleIndicatingTermination
}

const getQuestionnaireData = async () => {
  if (rawData.value || rawData.value.questionnaireId) {
    questionnaireData.value = rawData.value

    isLoading.value = true
    try {
      participantData.value = JSON.parse(JSON.stringify(questionnaireData.value.participants)) || []

      participantData.value.forEach((participant) => {
        participant.terminationDate = participant.terminationDate ? moment(participant.terminationDate).format('YYYY-MM-DD') : ''
        participant.hireDate = participant.hireDate ? moment(participant.hireDate).format('YYYY-MM-DD') : ''
      })

      contactData.value = JSON.parse(JSON.stringify(questionnaireData.value.contacts)) || []
      contactData.value.forEach((contact) => {
        if (contact?.noLongerAtBank) {
          contact.isDeleted = true
        }
        contact.phone = `${contact.phone}`.replace(/\D/g, '').slice(0, 10) || null
      })

      bankContactInformation.value = { ...questionnaireData.value.clientData }
    } catch (error) {
      console.error(error)
    } finally {
      isLoading.value = false
    }
  }
}

// Reset functions
const resetClientData = () => {
  bankContactInformation.value = JSON.parse(JSON.stringify(rawOriginalData.value.clientData))
}

const resetParticipantData = () => {
  participantData.value = JSON.parse(JSON.stringify(rawOriginalData.value.participants))
  participantData.value.forEach((participant) => {
    participant.terminationDate = participant.terminationDate ? moment(participant.terminationDate).format('YYYY-MM-DD') : ''
    participant.hireDate = participant.hireDate ? moment(participant.hireDate).format('YYYY-MM-DD') : ''
  })

  setTimeout(() => {
    handleBaseSalaryValidation()
  }, 250)
}

const resetContactData = () => {
  contactData.value = JSON.parse(JSON.stringify(rawOriginalData.value.contacts))
  contactData.value.forEach((contact) => {
    contact.phone = `${contact.phone}`.replace(/\D/g, '').slice(0, 10) || null
  })
}

const handleSave = async (isSubmitting = false, isAutoSaving = false, isClosing = false) => {
  handleBaseSalaryValidation()

  validateAllBankContactFields()

  handleGrids()

  bankContactInformationErrors.value = bankContactErrorCount.value

  const gridErrors = participantErrorCount.value + contactErrorCount.value + bankContactErrorCount.value

  if (gridErrors > 0) {
    if (!isAutoSaving) {
      errorToast(`Please fix the ${gridErrors} validation error(s) before ${isSubmitting ? 'submitting' : 'saving'}.`)

      if (contactErrorCount.value > 0) {
        setTimeout(() => {
          activeTab.value = 'contacts'
        }, 500)
      }

      if (participantErrorCount.value > 0) {
        setTimeout(() => {
          activeTab.value = 'participants'
        }, 1000)
      }

      if (bankContactErrorCount.value > 0) {
        setTimeout(() => {
          activeTab.value = 'company'
        }, 1500)
      }
    }

    return false
  }

  try {
    if (!isAutoSaving) {
      isLoading.value = true
    }

    const payload = {
      questionnaireId: questionnaireData.value.questionnaireId,
      clientData: bankContactInformation.value,
      contacts: contactData.value,
      participants: participantData.value,
    }

    payload.contacts.forEach((contact) => {
      const digits = `${contact.phone}`.replace(/\D/g, '').slice(0, 10) || null
      if (digits?.length === 10) {
        contact.phone = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      } else {
        contact.phone = digits
      }

      if (contact?.id === null) {
        contact.id = Math.floor(Math.random() * 1000000)
      }
    })

    payload.participants.forEach((participant) => {
      if (`${participant?.terminationReason}`.trim() === '') {
        participant.terminationReason = null
      }
    })

    const req = await axios.put('/Questionnaires/' + payload.questionnaireId, payload)

    if (req.status >= 200 && req.status < 300) {
      if (!isSubmitting) {
        rawOriginalData.value.participants = JSON.parse(JSON.stringify(payload.participants))

        rawOriginalData.value.contacts = JSON.parse(JSON.stringify(payload.contacts))
        rawOriginalData.value.contacts.forEach((contact) => {
          contact.phone = `${contact.phone}`.replace(/\D/g, '').slice(0, 10) || null
        })

        rawOriginalData.value.clientData = JSON.parse(JSON.stringify(payload.clientData))

        resetClientData()
        resetParticipantData()
        resetContactData()

        successToast(isAutoSaving ? 'Questionnaire saved automatically.' : 'Questionnaire saved successfully.')

        handleGrids()

        if (isClosing) {
          closeDialog('save')
        }
      }

      return true
    }

    errorToast('Failed to save the questionnaire. Please check the given information and try again later.')

    // Non-2xx status treated as failure
    return false
  } catch (error) {
    console.error('Error saving:', error)
    return false
  } finally {
    if (!isSubmitting) {
      isLoading.value = false
    }
    showCloseConfirmation.value = false
    handleGrids()
  }
}

const handleSubmit = async () => {
  isLoading.value = true
  showSubmitConfirmation.value = false
  isSubmitting.value = true

  const payload = {
    participants: participantData.value,
    contacts: contactData.value,
    bankContactInformation: bankContactInformation.value,
  }

  payload.contacts.forEach((contact) => {
    const digits = `${contact.phone}`.replace(/\D/g, '').slice(0, 10) || null
    if (digits?.length === 10) {
      contact.phone = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    } else {
      contact.phone = digits
    }

    if (contact?.id === null) {
      contact.id = Math.floor(Math.random() * 1000000)
    }
  })

  payload.participants.forEach((participant) => {
    if (`${participant?.terminationReason}`.trim() === '') {
      participant.terminationReason = null
    }
  })

  const saveResult = await handleSave(true, false, false)

  if (!saveResult) {
    isLoading.value = false
    return
  }

  try {
    isLoading.value = true
    const returnReq = await axios.post(`/Questionnaires/${questionnaireData.value.questionnaireId}/Return`, payload)

    if (returnReq.status >= 200 && returnReq.status < 300) {
      handleGrids()
      successToast('Questionnaire submitted successfully.')
      closeDialog('submit')

      setTimeout(() => {
        location.reload()
      }, 3000)
    } else {
      throw new Error('Failed to submit the questionnaire. Please try again later.')
    }
  } catch (error) {
    errorToast(error.message || 'Failed to submit the questionnaire. Please try again later.')
    console.error('Error submitting:', error)
    isLoading.value = false
    handleGrids()
  } finally {
    isLoading.value = false
    showCloseConfirmation.value = false
    showSubmitConfirmation.value = false
    isSubmitting.value = false
    handleGrids()
  }
}

const handleGrids = () => {
  setTimeout(async () => {
    await initializeGrids()
    isLoading.value = false
    setTimeout(() => {
      handleGridHeight()
    }, 100)
  }, 250)
}

// Add computed properties for error counts
const bankContactErrorCount = computed(() => {
  validateAllBankContactFields()
  return getBankContactValidationErrors()
})

const participantErrorCount = computed(() => {
  let errors = 0

  participantData.value.forEach((participant) => {
    if (participant.isFirstNameValid === false) errors++
    if (participant.isLastNameValid === false) errors++
    if (participant.isTitleValid === false) errors++
    if (participant.isHireDateValid === false) errors++
    if (participant.isTerminationDateValid === false) errors++
    if (participant.isTerminationReasonValid === false) errors++
    if (participant.isSalaryValid === false || participant.hasSalaryError === true) errors++
    if (participant.isBonusValid === false) errors++
    if (participant.isOtherCompensationValid === false) errors++
  })

  return errors
})

const contactErrorCount = computed(() => {
  let errors = 0

  contactData.value.forEach((contact) => {
    if (contact.isFirstNameValid === false) errors++
    if (contact.isLastNameValid === false) errors++
    if (contact.isTitleValid === false) errors++
    if (contact.isPhoneValid === false) errors++
    if (contact.isExtensionValid === false) errors++
    if (contact.isEmailValid === false) errors++
    if (contact.noLongerAtBankValid === false) errors++
    if (contact.isDeletedValid === false) errors++
  })

  return errors
})

const handleBaseSalaryValidation = () => {
  if (questionnaireData.value?.validateSalary) {
    participantData.value.forEach((participant, index) => {
      const isReadonly = isFieldReadonly(participant)
      const salaryValue = parseFloat(participant.salary)
      const hasError = (salaryValue <= 0 || isNaN(salaryValue)) && !isReadonly

      participant.hasSalaryError = hasError
    })

    handleGrids()
  }
}

const handleGridHeight = () => {
  const dialogBodyHeight = document.getElementById('dialog-body')?.clientHeight || 0
  const instructionsSectionHeight = document.querySelector('.instructions-section')?.clientHeight || 0
  const tabsHeight = document.querySelector('.tabs')?.clientHeight || 0
  const tabContentHeaderHeight = document.querySelector('.tab-header')?.clientHeight || 0

  if (dialogBodyHeight) {
    const height = dialogBodyHeight - (instructionsSectionHeight + tabsHeight + tabContentHeaderHeight + 145)
    const grids = document.querySelectorAll('.grid, .form-section')
    grids.forEach((grid) => {
      grid.style.height = `${height}px`
    })
  }
}

onBeforeMount(async () => {
  rawData.value = JSON.parse(JSON.stringify(props.rawQuestionnaireData || {}))
  rawOriginalData.value = JSON.parse(JSON.stringify(props.rawQuestionnaireData || {}))
  await getQuestionnaireData()
})

onUnmounted(() => {
  localStorage.removeItem('annualQuestionnaireIsOpen')
  stopAutoSave()
})

onMounted(async () => {
  localStorage.setItem('annualQuestionnaireIsOpen', 'true')
  startAutoSave()
  handleGrids()
})

const showCloseConfirmation = ref(false)

// Close handling methods
const handleCloseAttempt = () => {
  showCloseConfirmation.value = true
}

const confirmClose = async () => {
  isLoading.value = true
  rawData.value = props.rawQuestionnaireData || {}
  await getQuestionnaireData()

  handleGrids()

  setTimeout(() => {
    showCloseConfirmation.value = false
    closeDialog('close')

    if (store.state.attemptedSessionRefresh > 0) {
      logOut()
    }
  }, 1000)

  isLoading.value = false
}

const addNewContact = () => {
  const newContact = {
    id: Math.floor(Math.random() * 1000000),
    firstName: '',
    lastName: '',
    title: '',
    phone: undefined,
    email: undefined,
    noLongerAtBank: false,
    isDeleted: false,
  }

  contactData.value = [...contactData.value, newContact]
}
</script>

<style scoped lang="scss">
.dialog {
  display: flex;
  flex-direction: column;
  width: auto;
  height: auto;
  border-radius: 4px;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    padding: 10px;
    font-size: 14px;
    font-weight: 600;

    > div {
      display: flex;
      align-items: center;
      gap: 5px;

      > span {
        width: 20px;
      }
    }
  }

  .body {
    flex: 1;
    padding: 1rem;
    overflow-y: auto;
    background-color: white;
    max-height: v-bind(windowHeight);
    min-height: v-bind(windowHeight);
    width: v-bind(windowWidth);

    .instructions-section {
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid #e0e0e0;

      h1 {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
        color: #333;
      }

      span {
        color: #666;
        line-height: 1.4;
      }
    }

    .carousel-container {
      .tabs {
        margin-bottom: 0;

        .tab-link {
          position: relative;

          .error-badge {
            position: absolute;
            z-index: 10;
            top: 3px;
            right: 3px;
            background-color: #c20000;
            color: white;
            border-radius: 12px;
            padding: 2px 6px;
            font-size: 10px;
            font-weight: bold;
            line-height: 1;
          }
        }

        ul {
          border-bottom: 1px solid #dbdbdb;
        }

        li {
          a {
            border: 1px solid transparent;
            border-bottom: none;
            cursor: pointer;
            padding: 0.75em 1em;
            transition: all 0.2s ease;

            &:hover {
              background-color: #f5f5f5;
              border-color: #dbdbdb;
            }
          }

          &.is-active a {
            background-color: white;
            border-color: #dbdbdb;
            border-bottom-color: white;
            color: var(--bcc-color-default, #3273dc);
            font-weight: 600;
          }
        }
      }

      .carousel-content {
        overflow: hidden;
        border: 1px solid #dbdbdb;
        border-top: none;
        background: white;
        position: relative;
        height: auto;
      }

      .carousel-track {
        display: flex;
        width: 300%;
        transition: transform 0.3s ease-in-out;
      }

      .carousel-panel {
        width: 33.333333%;
        flex-shrink: 0;
        padding: 1rem;
        box-sizing: border-box;
        height: auto;

        .tab-header {
          margin-bottom: 1rem;

          .tab-header-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;

            > div {
              flex: 1;
            }
          }

          h3 {
            font-size: 1.125rem;
            font-weight: 600;
            color: #333;
            margin-bottom: 0.5rem;
          }

          p {
            color: #666;
            font-size: 0.875rem;
            line-height: 1.4;
          }
        }

        .tab-body {
          min-height: 400px;
          width: 100%;

          .grid {
            width: 100%;
            border: 1px solid #dbdbdb;
            border-radius: 4px;
          }

          .form-section {
            width: 100%;

            .field-group {
              width: 100% !important;

              .columns {
                .column {
                  &.is-4 {
                    h5 {
                      margin-bottom: 1rem;
                    }

                    .field {
                      margin-bottom: 0.75rem;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 10px;
    background-color: #ccc;
    border-top: 1px solid #666;
  }
}

:deep() {
  .grid {
    font-family: inherit;
    width: 100% !important;
    overflow: visible !important;

    .participant-first-name-validation-error,
    .participant-last-name-validation-error,
    .participant-title-validation-error,
    .participant-name-changed-validation-error,
    .participant-hire-date-validation-error,
    .participant-termination-date-validation-error,
    .participant-termination-reason-validation-error,
    .participant-salary-validation-error,
    .participant-bonus-validation-error,
    .participant-other-compensation-validation-error,
    .contact-primary-contact-validation-error,
    .contact-first-name-validation-error,
    .contact-last-name-validation-error,
    .contact-title-validation-error,
    .contact-phone-validation-error,
    .contact-phone-ext-validation-error,
    .contact-email-validation-error,
    .contact-no-longer-at-bank-validation-error,
    .contact-is-deleted-validation-error {
      border: 1px solid #ff000065 !important;
      border-radius: 4px !important;
    }

    .rgCell {
      font-size: 12px;
    }

    .rgHeaderCell {
      font-weight: 600;
      background-color: #f8f9fa;
    }

    revo-grid {
      width: 100% !important;
      height: 100% !important;
      display: block !important;
      overflow: visible !important;
    }

    .rgViewport {
      overflow: auto !important;
      width: 100% !important;
      height: 100% !important;
    }

    .rgScrollViewport {
      overflow: auto !important;
    }

    .rgRow {
      min-width: fit-content !important;
    }

    .rgHeaderRow,
    .rgDataRow {
      min-width: fit-content !important;
    }

    .rgTable {
      min-width: fit-content !important;
      table-layout: auto !important;
    }

    ::-webkit-scrollbar {
      width: 12px;
      height: 12px;
    }

    ::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    ::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 6px;
    }

    ::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
  }
}
</style>
